import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type {
  ToBillRow,
  WorkspaceCounts,
  WorkspaceFilters,
  WorkspaceInvoiceRow,
  WorkspacePage,
  WorkspaceStage,
} from '@jobos/shared';

/** Data access for the /invoices workspace. */

export const workspaceKeys = {
  all: ['invoice-workspace'] as const,
  counts: (filters: WorkspaceFilters) => [...workspaceKeys.all, 'counts', filters] as const,
  rows: (stage: WorkspaceStage, filters: WorkspaceFilters, page: number) =>
    [...workspaceKeys.all, 'rows', stage, filters, page] as const,
};

const toQuery = (filters: WorkspaceFilters, extra: Record<string, string> = {}) => {
  const params = new URLSearchParams(extra);
  for (const [key, value] of Object.entries(filters)) {
    if (value) params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
};

/**
 * Counts for all four tabs, filter-aware by contract (spec §6).
 *
 * `placeholderData: keepPreviousData` is what stops the tabs flashing zeros on
 * every filter change. A count that renders 0 and then 14 reads as "nothing to
 * do" for a moment, which is a lie the user acts on.
 */
export function useWorkspaceCounts(filters: WorkspaceFilters) {
  return useQuery({
    queryKey: workspaceKeys.counts(filters),
    queryFn: () => apiClient.get<WorkspaceCounts>(`/invoices/workspace/counts${toQuery(filters)}`),
    placeholderData: keepPreviousData,
  });
}

export function useWorkspaceRows<T = ToBillRow | WorkspaceInvoiceRow>(
  stage: WorkspaceStage,
  filters: WorkspaceFilters,
  page: number,
) {
  return useQuery({
    queryKey: workspaceKeys.rows(stage, filters, page),
    queryFn: () =>
      apiClient.get<WorkspacePage<T>>(
        `/invoices/workspace${toQuery(filters, { stage, page: String(page) })}`,
      ),
    // Same reason as the counts: the list should not blank out between pages.
    placeholderData: keepPreviousData,
  });
}

export const useToBillRows = (filters: WorkspaceFilters, page: number) =>
  useWorkspaceRows<ToBillRow>('to_bill', filters, page);

export const useInvoiceRows = (stage: WorkspaceStage, filters: WorkspaceFilters, page: number) =>
  useWorkspaceRows<WorkspaceInvoiceRow>(stage, filters, page);

/**
 * Everything the workspace shows is derived from the ledger, so anything that
 * moves money or lifecycle invalidates all of it — counts included. A settled
 * invoice must leave Sent and appear in Paid without a refresh, and that
 * means the tab numbers move with it.
 */
export function useInvalidateWorkspace() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: workspaceKeys.all });
}
